import useAcceptReactivationChallenges from 'features/reactivation/hooks/useAcceptReactivationChallenges';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { getLambdaHeaders } from 'shared/hooks/auth/useLambdaHeaders';
import { useAuth } from 'shared/providers/auth';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';

interface Challenge {
  challengeId: string;
  challengeType: string;
  challengeState: string;
  challengeContext: string;
}

export interface VerifySmsChallengeData {
  challengeId: string;
  identityId: string;
  acceptOptions: {
    otpOption?: {
      otpCode: string;
    };
    ssnVerifyOption?: {
      ssn: string;
    };
  };
}

export interface VerifySmsChallengeResult {
  success: boolean;
  errorCode?: number;
  message?: string;
  title?: string;
  data?: {
    challengeVerified?: boolean;
    challenges?: Challenge[];
    challengeContext?: string;
    title?: string;
    message?: string;
    errorCode?: number;
  };
  error?: Error;
}

// Error codes
export const EXPIRED_CODE_ERROR_CODE = 6;
export const INCORRECT_CODE_ERROR_CODE = 7;

export const FAILURE_MESSAGES = {
  EXPIRED_CODE: 'expired_code',
  INCORRECT_CODE: 'incorrect_code',
  UNKNOWN_ERROR: 'unknown_error',
};

const useVerifySmsChallenge = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const failureReasonRef = useRef<string>('');

  const { idToken, getVaroSessionId } = useAuth();
  const { t: translatedText } = useTranslation('otp');
  const { postAcceptReactivationChallenge } = useAcceptReactivationChallenges();

  //TODO: JT-135632-verify analytics for reactivation

  const logVerificationPhoneFailedEvent = usePressableAnalytics({
    analyticsId: 'phoneVerificationFailed',
    customAttributes: {
      get failure_reason() {
        return failureReasonRef.current || errorMessage || '';
      },
      verification_type: 'phone',
    },
  });

  const logVerificationPhoneSuccessfulEvent = usePressableAnalytics({
    analyticsId: 'verificationSuccessful',
    customAttributes: { verification_type: 'phone' },
  });

  const logVerificationError = (error: Error, failureReason?: string) => {
    // Set the failure reason in the ref before calling analytics
    failureReasonRef.current = failureReason || '';

    logVerificationPhoneFailedEvent();

    // Clear the ref after using it
    failureReasonRef.current = '';

    logBreadcrumb({
      level: 'error',
      message: `Reactivation phone challenge error: ${error.message}`,
    });
  };

  const verifyChallenge = async (
    data: VerifySmsChallengeData,
  ): Promise<VerifySmsChallengeResult> => {
    setIsLoading(true);
    setErrorMessage(null);
    setIsSuccess(false);

    try {
      const varoSessionId = await getVaroSessionId();
      const headers = await getLambdaHeaders(idToken, varoSessionId);

      const acceptOptions: {
        otpOption?: { otpCode: string };
        ssnVerifyOption?: { ssn: string };
      } = {};
      if (data.acceptOptions?.otpOption) {
        acceptOptions.otpOption = {
          otpCode: data.acceptOptions.otpOption.otpCode,
        };
      }
      if (data.acceptOptions?.ssnVerifyOption) {
        acceptOptions.ssnVerifyOption = {
          ssn: data.acceptOptions.ssnVerifyOption.ssn,
        };
      }

      const verifyReactivationResult = await postAcceptReactivationChallenge(
        {
          challengeId: data.challengeId,
          identityId: data.identityId,
          acceptOptions,
        },
        headers,
      );

      const { message, title, errorCode, challengeVerified } =
        verifyReactivationResult;
      const numericErrorCode = errorCode ? Number(errorCode) : undefined;

      if (message) {
        setErrorMessage(message);
      }

      if (numericErrorCode === EXPIRED_CODE_ERROR_CODE) {
        logVerificationError(
          new Error(message || 'Expired code'),
          FAILURE_MESSAGES.EXPIRED_CODE,
        );

        return {
          success: false,
          errorCode: numericErrorCode,
          message,
          title,
          data: {
            ...verifyReactivationResult,
            errorCode: numericErrorCode,
          },
        };
      } else if (numericErrorCode === INCORRECT_CODE_ERROR_CODE) {
        logVerificationError(
          new Error(message || 'Incorrect code'),
          FAILURE_MESSAGES.INCORRECT_CODE,
        );

        return {
          success: false,
          errorCode: numericErrorCode,
          message,
          title,
          data: {
            ...verifyReactivationResult,
            errorCode: numericErrorCode,
          },
        };
      }

      if (challengeVerified) {
        logVerificationPhoneSuccessfulEvent();
        setIsSuccess(true);
        return {
          success: true,
          data: {
            ...verifyReactivationResult,
            errorCode: numericErrorCode,
          },
        };
      }

      // FAILURE: unknown_error
      logVerificationError(
        new Error(message || 'Unknown error'),
        FAILURE_MESSAGES.UNKNOWN_ERROR,
      );

      return {
        success: false,
        errorCode: numericErrorCode,
        title: title || '',
        data: {
          ...verifyReactivationResult,
          errorCode: numericErrorCode,
          message: message,
        },
      };
    } catch (error) {
      const typedError =
        error instanceof Error ? error : new Error(String(error));

      logBreadcrumb({
        level: 'error',
        message: `Reactivation challenge error: ${typedError.message}`,
      });

      setErrorMessage(translatedText('genericError'));
      return {
        success: false,
        error: typedError,
        data: {
          message: translatedText('genericError'),
        },
      };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    verifyChallenge,
    isLoading,
    isSuccess,
    errorMessage,
    setErrorMessage,
  };
};

export default useVerifySmsChallenge;
